/**
 * membraneDrumPipeline — a circular drum head that visibly vibrates
 * with bass pulses. Concentric ripple modes spread from the strike
 * points; mid-band adds harmonic secondary modes.
 *
 * Renders an SDF disk in 3D, with radial wave packets and a hard rim.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MembraneDrumPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// Concentric mode shape — combination of Bessel-like radial modes.
float drumMode(vec2 q, float t, float amp) {
  float r = length(q);
  float a = 0.0;
  // Fundamental mode (m=1).
  a += sin(r * 6.0 - t * 8.0) * exp(-r * 1.5) * amp;
  // Secondary mode (m=2).
  a += sin(r * 10.0 - t * 12.0) * exp(-r * 1.8) * amp * 0.5;
  return a;
}

float mapMembrane(vec3 p) {
  // Infinite plane; we treat z as height.
  // Disk radius 1.0; outside the disk we push the surface away.
  float r = length(p.xy);
  float rim = smoothstep(1.0, 0.98, r);
  float base = max(0.0, 1.0 - r * 2.0) * 1.5; // outside the disk, no surface
  float z = 0.0;
  // Bass strikes near random points on the disk.
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  bass = pow(clamp(bass * 2.4, 0.0, 1.0), 0.65);
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  mid = pow(clamp(mid * 2.6, 0.0, 1.0), 0.8);
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  high = pow(clamp(high * 3.6, 0.0, 1.0), 0.85);
  float t = uTime;
  vec2 s1 = 0.6 * vec2(cos(t * 0.5), sin(t * 0.42));
  vec2 s2 = 0.6 * vec2(cos(t * 0.37 + 2.1), sin(t * 0.55));
  vec2 s3 = 0.6 * vec2(cos(t * 0.61 - 1.7), sin(t * 0.33 + 0.9));
  z += drumMode((p.xy - s1) / 0.5, t, uBass.x * bass) * exp(-length(p.xy - s1) * 2.0);
  z += drumMode((p.xy - s2) / 0.5, t, uBass.y * bass) * exp(-length(p.xy - s2) * 2.0);
  z += drumMode((p.xy - s3) / 0.5, t, uBass.z * bass) * exp(-length(p.xy - s3) * 2.0);
  // Mid-band harmonic overlay.
  z += sin(length(p.xy) * 14.0 - t * 7.0) * exp(-r * 1.2) * mid * 0.06;
  // High-band micro ripples.
  z += sin(p.x * 28.0 + t * 3.0) * sin(p.y * 26.0 - t * 2.5) * 0.004 * high;
  // Combine: the membrane's surface is at z=z, outside the disk we
  // push z toward -1 so no ray hits there.
  float target = mix(-2.0, z, rim);
  // SDF of a height-field: vertical distance to the surface z.
  return p.z - target;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    mapMembrane(p + e.xyy) - mapMembrane(p - e.xyy),
    mapMembrane(p + e.yxy) - mapMembrane(p - e.yxy),
    mapMembrane(p + e.yyx) - mapMembrane(p - e.yyx)
  ));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  uv.x *= uResolution.x / uResolution.y;
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.4, 0.0, 1.0), 0.65);
  mid = pow(clamp(mid * 2.6, 0.0, 1.0), 0.8);
  high = pow(clamp(high * 3.6, 0.0, 1.0), 0.85);

  // Camera looking down at an angle.
  vec3 ro = vec3(0.0, -1.8, 1.5 + 0.1 * sin(uTime * 0.2));
  vec3 ww = normalize(vec3(0.0, 1.0, 0.0) - ro);
  vec3 uu = normalize(cross(ww, vec3(0.0, 0.0, 1.0)));
  vec3 vv = cross(uu, ww);
  vec3 rd = normalize(uv.x * uu + uv.y * vv + 1.7 * ww);

  float t = 0.0;
  bool hit = false;
  vec3 p = ro;
  for (int i = 0; i < 110; i++) {
    float d = mapMembrane(p);
    if (d < 0.0008) { hit = true; break; }
    if (t > 9.0) break;
    t += d * 0.9;
    p = ro + rd * t;
  }
  vec3 col = vec3(0.015, 0.018, 0.03);
  if (hit) {
    vec3 n = calcNormal(p);
    vec3 ldir = normalize(vec3(0.4, 0.6, 0.8));
    float diff = clamp(dot(n, ldir), 0.0, 1.0);
    float fres = pow(1.0 - clamp(dot(n, -rd), 0.0, 1.0), 2.0);
    vec3 h = normalize(ldir - rd);
    float spec = pow(clamp(dot(n, h), 0.0, 1.0), 32.0);
    // Concentric mode visibility — bright rings on the membrane.
    float rings = 0.5 + 0.5 * sin(length(p.xy) * 18.0 - uTime * 6.0);
    col = vec3(0.0);
    col += diff * vec3(0.6, 0.7, 0.85);
    col += vec3(0.85, 0.55, 0.95) * pow(rings, 4.0) * (0.2 + bass * 0.6);
    col += fres * vec3(0.55, 0.75, 1.0) * (0.4 + mid * 0.8);
    col += spec * vec3(1.0) * (0.3 + high * 0.7);
    // Rim glow.
    float rimGlow = smoothstep(0.94, 1.0, length(p.xy));
    col += vec3(1.0, 0.7, 0.5) * rimGlow * (0.2 + bass * 0.4);
  }

  float vig = pow(16.0 * vUV.x * (1.0 - vUV.x) * vUV.y * (1.0 - vUV.y), 0.25);
  col *= vig;
  col = col / (1.0 + col);
  col = pow(col, vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}`;

export function computeMembraneDrumBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.95);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.85));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.6);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.15 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.75 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.6 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 4.0 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 6.0 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 8.0 + 2.1);
}

export function computeMembraneDrumRealtimeBands(freqData: Uint8Array, out: Float32Array): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.008], [0.008, 0.030], [0.030, 0.060],
    [0.060, 0.150], [0.150, 0.260], [0.260, 0.420],
    [0.420, 0.620], [0.620, 0.800], [0.800, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    let s = 0, c = 0;
    const a = Math.floor(lo * N);
    const b = Math.floor(hi * N);
    for (let k = a; k < b; k++) { s += freqData[k]; c++; }
    out[i] = c > 0 ? (s / c) / 255 : 0;
  }
}

export function createMembraneDrumPipeline(gl: WebGL2RenderingContext): MembraneDrumPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawMembraneDrum(
  gl: WebGL2RenderingContext,
  pipe: MembraneDrumPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}